import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { formatDate, formatDateTime } from 'src/utils';
import { UserLoginDto } from 'src/api/FiretrolApiClient';
import {
  LOGIN_ARCHIVE_SIZE,
  useAllUserLogins,
} from 'src/api/queries/admin/useAllUserLogins';
import { DivisionList } from 'src/app/organization/DivisionList';
import { OfficeList } from 'src/app/organization/OfficeList';
import { Header } from 'src/design/organisms/Header';
import { ArchiveNavigator } from 'src/design/molecules/ArchiveNavigator';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';

const getColumns = (): MRT_ColumnDef<UserLoginDto>[] => {
  return [
    {
      id: 'user',
      accessorFn: (row) => row.user?.fullName,
      header: 'User',
    },
    {
      accessorKey: 'createdAt',
      header: 'Login Date',
      Cell: ({ cell }) => formatDateTime(cell.getValue<string>()),
    },
    {
      accessorKey: 'ip',
      header: 'IP Address',
    },
    {
      id: 'offices',
      header: 'Offices',
      filterFn: 'contains',
      enableColumnFilterModes: false,
      accessorFn: (row) => {
        return row.user?.userOrganizationOffice
          ?.flatMap((o) => o.organizationOffice?.name)
          .join(', ');
      },
      Cell: ({ cell }) => {
        const login = cell.row.original;
        return (
          <OfficeList
            offices={login.user?.userOrganizationOffice?.flatMap(
              (o) => o.organizationOffice,
            )}
          />
        );
      },
    },
    {
      id: 'divisions',
      header: 'Divisions',
      filterFn: 'contains',
      enableColumnFilterModes: false,
      accessorFn: (row) => {
        return row.user?.userDivision
          ?.flatMap((d) => d.division?.name)
          .join(', ');
      },
      Cell: ({ cell }) => {
        const login = cell.row.original;
        return (
          <DivisionList
            divisions={login.user?.userDivision?.flatMap((d) => d.division)}
          />
        );
      },
    },
  ];
};

export const UserHistory: React.FC = () => {
  const [archive, setArchive] = useState(1);
  const loginsQuery = useAllUserLogins(archive);
  const logins = useMemo(
    () => loginsQuery.data?.items ?? [],
    [loginsQuery.data],
  );
  const loading = loginsQuery.isFetching;

  // Compute date range from loaded records (newest to oldest within the archive)
  const dateRange = useMemo(() => {
    if (logins.length === 0) return undefined;
    const newestDate = logins[0]?.createdAt;
    const oldestDate = logins[logins.length - 1]?.createdAt;
    if (!newestDate || !oldestDate) return undefined;
    const newest = moment(newestDate);
    const oldest = moment(oldestDate);
    if (newest.isSame(oldest, 'month')) {
      return formatDate(newestDate, 'MMMM YYYY');
    }
    return `${formatDate(oldestDate, 'MMM YYYY')} - ${formatDate(
      newestDate,
      'MMM YYYY',
    )}`;
  }, [logins]);

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'user-history',
    columns: columns,
    defaultSorting: [{ id: 'createdAt', desc: true }],
  });

  const tableExports = useTableExports<UserLoginDto>(
    'all-users-login-history',
    (data) => {
      return data.map((item) => ({
        ['User']: item.user.fullName,
        ['Login Date']: formatDateTime(item.createdAt),
        ['IP Address']: item.ip,
        ['Offices']: item.user.userOrganizationOffice
          ?.flatMap((o) => o.organizationOffice?.name)
          .join(' | '),
        ['Divisions']: item.user.userDivision
          ?.flatMap((d) => d.division?.name)
          .join(' | '),
      }));
    },
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: false,
      enableClickToCopy: false,
      columns: columns,
      data: logins,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
    },
    totalCount: logins.length,
    isLoading: loginsQuery.isLoading,
    isFetching: loginsQuery.isFetching,
    isError: loginsQuery.isError,
    ...tableExports,
  });

  return (
    <>
      <Header
        breadcrumbProps={{
          items: [{ text: 'Settings' }, { text: 'User Login History' }],
        }}
        title="User Login History"
        helpText="View login history for all users in your organization"
      />
      <Stack>
        <Paper>
          <ArchiveNavigator
            page={loginsQuery.data?.page ?? archive}
            totalPages={loginsQuery.data?.totalPages ?? 0}
            totalCount={loginsQuery.data?.totalCount ?? 0}
            pageSize={LOGIN_ARCHIVE_SIZE}
            loading={loading}
            onPageChange={setArchive}
            dateRange={dateRange}
          />
        </Paper>
        {table}
      </Stack>
    </>
  );
};
